import React from 'react';
import { useNavigate } from 'react-router-dom';
import { journeyService } from '../services/journeyService';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import Icon from '../components/ui/Icon';
import Dropdown from '../components/ui/Dropdown';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EventBus from '../utils/EventBus';
import styles from './Journeys.module.css';

const FILTERS = ['all', 'draft', 'active', 'paused', 'stopped'];

class JourneyCardSkeleton extends React.Component {
  render() {
    return (
      <div className={styles.cardSk}>
        <div className={styles.cardSkHead}>
          <Skeleton variant="circle" width={40} height={40} />
          <div>
            <Skeleton variant="text" width={160} height={14} />
            <Skeleton variant="text" width={120} height={11} style={{ marginTop: 6 }} />
          </div>
        </div>
        <Skeleton variant="rect" width="100%" height={1} style={{ margin: '12px 0' }} />
        <div className={styles.cardSkStats}>
          {[0, 1].map((i) => (
            <div key={i}>
              <Skeleton variant="text" width={50} height={18} />
              <Skeleton variant="text" width={70} height={10} style={{ marginTop: 4 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

class JourneyCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { stats: null };
  }

  componentDidMount() {
    journeyService.stats(this.props.j.id).then((stats) => this.setState({ stats })).catch(() => {});
  }

  render() {
    const { j, onOpen, menuItems } = this.props;
    const stats = this.state.stats || { users_in_journey: 0, completed: 0, link_clicks: 0 };

    return (
      <div className={styles.card}>
        <div className={styles.cardTop}>
          <div className={styles.cardIco} style={{ background: 'rgba(184,66,66,.08)' }}>
            <Icon name="route" size={20} />
          </div>
          <div className={styles.cardInfo}>
            <div className={styles.cardName}>{j.name}</div>
            <div className={styles.cardMeta}>{(j.config?.steps?.length || 0)} steps</div>
          </div>
          <Badge status={j.status} dot>{j.status}</Badge>
        </div>
        <div className={styles.divider} />
        <div className={styles.cardStats}>
          <div className={styles.stat}>
            <div className={styles.statVal}>{stats.users_in_journey}</div>
            <div className={styles.statLbl}>In Progress</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal} style={{ color: 'var(--greenHi)' }}>{stats.completed}</div>
            <div className={styles.statLbl}>Completed</div>
          </div>
          {/* Inferred from a short link tagged into a send_message step's message
              (WhatsApp/SMS give no native click signal) — see ShortLinkField.jsx. */}
          <div className={styles.stat}>
            <div className={styles.statVal}>{stats.link_clicks}</div>
            <div className={styles.statLbl}>Link Clicks</div>
          </div>
        </div>
        <div className={styles.cardActions}>
          <button className={styles.openBtn} onClick={() => onOpen(j.id)}>
            Open Builder →
          </button>
          <Dropdown
            align="right"
            trigger={<button className={styles.menuBtn}>⋯</button>}
            items={menuItems}
          />
        </div>
      </div>
    );
  }
}

class Journeys extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, filter: 'all', journeys: [], confirmDelete: null, confirmStop: null, acting: false };
    this.load = this.load.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  load() {
    return journeyService.getAll({ limit: 100 })
      .then((journeys) => this.setState({ loading: false, journeys }))
      .catch(() => this.setState({ loading: false }));
  }

  filtered() {
    const { filter, journeys } = this.state;
    if (filter === 'all') return journeys;
    return journeys.filter((j) => j.status === filter);
  }

  act(promise, successMessage) {
    this.setState({ acting: true });
    promise
      .then(() => {
        this.setState({ acting: false, confirmDelete: null, confirmStop: null });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  menuItems(j) {
    const items = [];
    if (j.status === 'draft') {
      items.push({ label: 'Activate', onClick: () => this.act(journeyService.activate(j.id), 'Journey activated.') });
    }
    if (j.status === 'active') {
      items.push({ label: 'Pause', onClick: () => this.act(journeyService.pause(j.id), 'Journey paused.') });
    }
    if (j.status === 'paused') {
      items.push({ label: 'Resume', onClick: () => this.act(journeyService.activate(j.id), 'Journey resumed.') });
    }
    if (j.status !== 'stopped') {
      items.push({ divider: true }, { label: 'Stop', danger: true, onClick: () => this.setState({ confirmStop: j }) });
    }
    items.push({ label: 'Delete', danger: true, onClick: () => this.setState({ confirmDelete: j }) });
    return items;
  }

  render() {
    const { loading, filter, journeys, confirmDelete, confirmStop, acting } = this.state;
    const rows = this.filtered();

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Journeys</div>
            <div className={styles.pageSub}>Multi-step automated lifecycle flows</div>
          </div>
          <button className={styles.newBtn} onClick={() => this.props.navigate('new')}>+ New Journey</button>
        </div>

        <div className={styles.tabs}>
          {FILTERS.map((f) => (
            <button
              key={f}
              className={[styles.tab, filter === f ? styles.tabOn : ''].join(' ')}
              onClick={() => this.setState({ filter: f })}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && (
                <span className={styles.tabCount}>
                  {journeys.filter((j) => j.status === f).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.grid}>
          {loading
            ? [0, 1, 2, 3].map((i) => <JourneyCardSkeleton key={i} />)
            : rows.length === 0
              ? <div className={styles.emptyWrap}><EmptyState icon="route" title="No journeys" description="Create your first automated journey." /></div>
              : rows.map((j) => <JourneyCard key={j.id} j={j} onOpen={this.props.navigate} menuItems={this.menuItems(j)} />)
          }
        </div>

        <ConfirmDialog
          open={!!confirmDelete}
          title="Delete journey?"
          message={confirmDelete ? `"${confirmDelete.name}" will be removed. This can't be undone.` : ''}
          confirmLabel="Delete"
          loading={acting}
          onConfirm={() => this.act(journeyService.remove(confirmDelete.id), 'Journey deleted.')}
          onCancel={() => this.setState({ confirmDelete: null })}
        />

        <ConfirmDialog
          open={!!confirmStop}
          title="Stop journey?"
          message={confirmStop ? `"${confirmStop.name}" will stop enrolling and messaging users immediately. This can't be undone.` : ''}
          confirmLabel="Stop journey"
          loading={acting}
          onConfirm={() => this.act(journeyService.stop(confirmStop.id), 'Journey stopped.')}
          onCancel={() => this.setState({ confirmStop: null })}
        />
      </>
    );
  }
}

function JourneysWithNav(props) {
  const navigate = useNavigate();
  return <Journeys {...props} navigate={(id) => navigate('/journeys/' + id + '/builder')} />;
}

export default JourneysWithNav;
